import React, { useRef, useState } from "react";
import { DataTable, DataTablePageEvent, DataTableSortEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import { MultiSelect } from "primereact/multiselect";

export interface HistoryDataRow {
  id: number;
  posId: number;
  vendorId: number;
  rawMaterialId: number;
  invoiceNumber: string;
  purchaseDate: string;
  invoicedQuantity: number;
  basicAmount: number;
  taxAmount: number;
  totalAmount: number;
  acceptedQuantity: number | null;
  rejectedQuantity: number;
  utilizedQuantity: number;
  utilizedQuantityDisplay: string;
  invoicedRemainingQty: number;
  acceptedRemainingQty: number;
  remainingQty: number;
  vendorName: string;
  rawMaterialName: string;
  posAddress: string;
}

interface HistoryDatatableProps {
  data: HistoryDataRow[];
  loading: boolean;
  totalRecords?: number;
  page?: number;
  rows?: number;
  onPage?: (event: DataTablePageEvent) => void;
  onSort?: (event: DataTableSortEvent) => void;
  selectedUnitId?: string | null;
  sortField?: string | null;
  sortOrder?: 1 | -1 | 0;
  hasSearched?: boolean; 
}

const RawMaterialHistoryDatatable: React.FC<HistoryDatatableProps> = ({
  data,
  loading,
  totalRecords = 0,
  page = 0,
  rows = 10,
  onPage,
  onSort,
  selectedUnitId,
  sortField,
  sortOrder,
  hasSearched
}) => {
  const toast = useRef<Toast>(null);
  
  // State for column selection
  const [selectedColumns, setSelectedColumns] = useState<any[]>([]);
  const [allAvailableColumns, setAllAvailableColumns] = useState<any[]>([]);

  // Define all available columns
  const baseColumns = [
    { field: "posAddress", header: "Unit", sortable: true, style: { minWidth: "130px" } },
    { field: "purchaseDate", header: "Purchase Date", sortable: true, style: { minWidth: "140px" } },
    { field: "invoiceNumber", header: "Invoice No", sortable: true, style: { minWidth: "130px" } },
    { field: "vendorName", header: "Vendor Name", sortable: true, style: { minWidth: "160px" } },
    { field: "rawMaterialName", header: "Raw Material", sortable: true, style: { minWidth: "160px" } },
    { field: "basicAmount", header: "Basic Amount", sortable: true, style: { minWidth: "130px" } },
    { field: "taxAmount", header: "Tax Amount", sortable: true, style: { minWidth: "130px" } },
    { field: "totalAmount", header: "Total Amount", sortable: true, style: { minWidth: "130px" } },
    { field: "invoicedQuantity", header: "Invoiced Qty", sortable: true, style: { minWidth: "130px" } },
    { field: "acceptedQuantity", header: "Accepted Qty", sortable: true, style: { minWidth: "130px" } },
    { field: "rejectedQuantity", header: "Rejected Qty", sortable: true, style: { minWidth: "130px" } },
    { field: "utilizedQuantityDisplay", header: "Utilized Qty (%)", sortable: true, style: { minWidth: "140px" } },
    { field: "invoicedRemainingQty", header: "Remaining Qty", sortable: true, style: { minWidth: "160px" } },
    { field: "acceptedRemainingQty", header: "Unutilized Qty", sortable: true, style: { minWidth: "160px" } },
  ];

  React.useEffect(() => {
    setAllAvailableColumns(baseColumns);
    setSelectedColumns(baseColumns); 
  }, []);

  const handlePage = (event: DataTablePageEvent) => {
    if (onPage) onPage(event);
  };

const handleSort = (event: DataTableSortEvent) => {
  if (!onSort) return;
  if (sortField === event.sortField) {
    const newOrder = sortOrder === 1 ? -1 : 1;
    onSort({ ...event, sortOrder: newOrder });
  } else {
    onSort({ ...event, sortOrder: 1 });
  }
};

  const dateBody = (rowData: HistoryDataRow) =>
    rowData.purchaseDate ? new Date(rowData.purchaseDate).toLocaleDateString("en-GB") : "-";

  const amountBody = (rowData: HistoryDataRow, field: string) => {
    const value = rowData[field as keyof HistoryDataRow];
    if (typeof value === "number") {
      return value.toLocaleString("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 2,
      });
    }
    return "-";
  };

  const quantityBody = (rowData: HistoryDataRow, field: string) => {
    const value = rowData[field as keyof HistoryDataRow];
    if (typeof value === "number") {
      return value.toLocaleString("en-IN");
    }
    return value === null ? "-" : "0";
  };

  const utilizedQtyBody = (rowData: HistoryDataRow) => rowData.utilizedQuantityDisplay || "0.00 (0%)";



 
  const columnBodyTemplate = (rowData: HistoryDataRow, column: any) => {
    const value = rowData[column.field as keyof HistoryDataRow];
    
    switch (column.field) {
      case "purchaseDate":
        return dateBody(rowData);
      case "basicAmount":
      case "taxAmount":
      case "totalAmount":
        return amountBody(rowData, column.field);
      case "invoicedQuantity":
      case "acceptedQuantity":
      case "rejectedQuantity":
      case "invoicedRemainingQty":
      case "acceptedRemainingQty":
        return quantityBody(rowData, column.field);
      case "utilizedQuantityDisplay":
        return utilizedQtyBody(rowData);
      default:
        return value || "-";
    }
  };

  return (
    <div className="p-3 w-full">
      <Toast ref={toast} />
      <Card className="shadow-md border border-gray-300">
        <div className="flex justify-content-between align-items-center mb-3">
         
          <MultiSelect
            value={selectedColumns}
            options={allAvailableColumns}
            optionLabel="header"
            onChange={(e) => setSelectedColumns(e.value)}
            display="chip"
            placeholder="Select Columns"
            className="w-20rem"
            maxSelectedLabels={3}
            selectedItemsLabel="{0} columns selected"
          />
        </div>
        
        <DataTable
          value={data}
          lazy
          paginator
          loading={loading}
          totalRecords={totalRecords}
          first={page * rows}
          rows={rows}
          onPage={handlePage}
          onSort={handleSort}
          sortField={sortField ?? undefined}
          sortOrder={sortOrder ?? 0}
          sortMode="single"
          removableSort={false}
         
          className="text-sm border border-gray-300"
          rowsPerPageOptions={[10, 25, 50, 100, 200]}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
          showGridlines
          scrollable
          scrollHeight="flex"
          tableStyle={{ minWidth: "1200px" }}
           emptyMessage={
    hasSearched
      ? "No matching records found."
      : "Please select at least one manufacturing unit and click the search icon to view the report."
  }
        >
          {selectedColumns.map((col) => (
            <Column
              key={col.field}
              field={col.field}
              header={col.header}
              sortable={col.sortable}
              body={(rowData) => columnBodyTemplate(rowData, col)}
              style={col.style}
            />
          ))}
        </DataTable>
      </Card>
    </div>
  );
};

export default RawMaterialHistoryDatatable;